/**
 * Safe Storage Utilities
 * Wraps localStorage and sessionStorage with error handling
 * Prevents crashes in private browsing mode or when storage is disabled
 */

import { logger } from '../infrastructure/logger';

/**
 * Safely gets an item from localStorage
 * @param key - Storage key
 * @returns The stored value or null if not found/error
 */
export function safeLocalStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    // Storage might be disabled or in private mode
    logger.warn(`[SafeStorage] Failed to get ${key} from localStorage:`, error);
    return null;
  }
}

/**
 * Safely sets an item in localStorage
 * @param key - Storage key
 * @param value - Value to store
 * @returns true if successful, false otherwise
 */
export function safeLocalStorageSet(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    // Storage might be full, disabled, or in private mode
    logger.warn(`[SafeStorage] Failed to set ${key} in localStorage:`, error);
    return false;
  }
}

/**
 * Safely removes an item from localStorage
 * @param key - Storage key
 * @returns true if successful or item didn't exist, false on error
 */
export function safeLocalStorageRemove(key: string): boolean {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    logger.warn(`[SafeStorage] Failed to remove ${key} from localStorage:`, error);
    return false;
  }
}

/**
 * Safely clears localStorage
 * @returns true if successful, false otherwise
 */
export function safeLocalStorageClear(): boolean {
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    logger.warn('[SafeStorage] Failed to clear localStorage:', error);
    return false;
  }
}

/**
 * Safely gets an item from sessionStorage
 * @param key - Storage key
 * @returns The stored value or null if not found/error
 */
export function safeSessionStorageGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch (error) {
    logger.warn(`[SafeStorage] Failed to get ${key} from sessionStorage:`, error);
    return null;
  }
}

/**
 * Safely sets an item in sessionStorage
 * @param key - Storage key
 * @param value - Value to store
 * @returns true if successful, false otherwise
 */
export function safeSessionStorageSet(key: string, value: string): boolean {
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch (error) {
    logger.warn(`[SafeStorage] Failed to set ${key} in sessionStorage:`, error);
    return false;
  }
}

/**
 * Safely removes an item from sessionStorage
 * @param key - Storage key
 * @returns true if successful or item didn't exist, false on error
 */
export function safeSessionStorageRemove(key: string): boolean {
  try {
    sessionStorage.removeItem(key);
    return true;
  } catch (error) {
    logger.warn(`[SafeStorage] Failed to remove ${key} from sessionStorage:`, error);
    return false;
  }
}

/**
 * Safely clears sessionStorage
 * @returns true if successful, false otherwise
 */
export function safeSessionStorageClear(): boolean {
  try {
    sessionStorage.clear();
    return true;
  } catch (error) {
    logger.warn('[SafeStorage] Failed to clear sessionStorage:', error);
    return false;
  }
}

/**
 * Typed storage helper for JSON values
 */
export const safeStorage = {
  local: {
    get: safeLocalStorageGet,
    set: safeLocalStorageSet,
    remove: safeLocalStorageRemove,
    clear: safeLocalStorageClear,
    
    /**
     * Get and parse JSON value
     */
    getObject<T>(key: string): T | null {
      const value = safeLocalStorageGet(key);
      if (!value) return null;
      try {
        return JSON.parse(value) as T;
      } catch {
        logger.warn(`[SafeStorage] Failed to parse JSON for key ${key}`);
        return null;
      }
    },
    
    /**
     * Stringify and set JSON value
     */
    setObject<T>(key: string, value: T): boolean {
      try {
        const json = JSON.stringify(value);
        return safeLocalStorageSet(key, json);
      } catch {
        logger.warn(`[SafeStorage] Failed to stringify value for key ${key}`);
        return false;
      }
    }
  },
  
  session: {
    get: safeSessionStorageGet,
    set: safeSessionStorageSet,
    remove: safeSessionStorageRemove,
    clear: safeSessionStorageClear,
    
    getObject<T>(key: string): T | null {
      const value = safeSessionStorageGet(key);
      if (!value) return null;
      try {
        return JSON.parse(value) as T;
      } catch {
        logger.warn(`[SafeStorage] Failed to parse JSON for key ${key}`);
        return null;
      }
    },
    
    setObject<T>(key: string, value: T): boolean {
      try {
        const json = JSON.stringify(value);
        return safeSessionStorageSet(key, json);
      } catch {
        logger.warn(`[SafeStorage] Failed to stringify value for key ${key}`);
        return false;
      }
    }
  }
};

export default safeStorage;
